// ─── E3.1.2: LV-PDF-Parser (reine Logik, keine UI) ────────────────────────────
//
// Parst ein LV/Auftrags-PDF komplett IM BROWSER (pdfjs-dist via CDN, analog
// zum XLSX-Lazy-Load in BaustellenAuswertung). Die PDF-Datei verlaesst den
// Rechner nie - ans Backend gehen spaeter nur die in der Vorschau (E3.1.3)
// bestaetigten Zeilen.
//
// Layout-Familie (E3.0-Analyse): AVA-Export mit Spaltenkopf
// "Ordnungszahl | Menge ME | Einheitspreis | Gesamtpreis". Pro Position:
//   [OZ-Spalte] OZ-Nummer   [Text-Spalte] Kurztext
//                           [Text-Spalte] Langtext-Zeile(n)
//               [Menge+ME]              [EP]        [GP]   <- Abschlusszeile
// Die Mengen-/Preiszeile terminiert die Position - dadurch sind Seiten-
// umbrueche mitten in Positionen unkritisch (Parser sammelt weiter, bis der
// Terminator kommt).
//
// Sicherheitsprinzip: Der Parser BRICHT HART AB (LvParseError), wenn das
// Layout nicht zweifelsfrei erkannt wird - kein stilles Falsch-Parsen:
//   1. Spaltenkopf nicht gefunden -> Abbruch (er ist zugleich Kalibrierung
//      der Spalten-x-Anker).
//   2. Keine einzige vollstaendige Position -> Abbruch.
//   3. Mehr verworfene OZ-Kandidaten als erkannte Positionen -> Abbruch.
// Kopfdaten-Heuristiken (Bauvorhaben, Adresse, ...) duerfen dagegen leer
// ausfallen - sie werden in der Vorschau manuell ergaenzt.

// Versioniert/pinned - bewusst keine "latest"-URL. 3.11.174 ist der letzte
// UMD-Build (window.pdfjsLib), der sich wie XLSX per Script-Tag laden laesst.
const PDFJS_VERSION = '3.11.174';
const PDFJS_URL = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.min.js`;
const PDFJS_WORKER_URL = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.worker.min.js`;

export class LvParseError extends Error {
  constructor(message) {
    super(message);
    this.name = 'LvParseError';
  }
}

// Warnungen pro Position (Anzeige in der Vorschau) - Regeln identisch fuer
// PDF- (E3.1) und Tabellen-Import (E3.2). Mutiert p.warnungen in-place,
// Verhalten byte-gleich zum frueheren Inline-Block in parseLvPdfDocument.
export function berechnePositionsWarnungen(positionen) {
  const posNrCount = new Map();
  for (const p of positionen) {
    if (p.pos_nr) posNrCount.set(p.pos_nr, (posNrCount.get(p.pos_nr) || 0) + 1);
  }
  for (const p of positionen) {
    p.warnungen = [];
    const rechnerisch = p.sollmenge * p.einheitspreis;
    const toleranz = Math.max(1, p.gesamtpreis * 0.005);
    if (Math.abs(rechnerisch - p.gesamtpreis) > toleranz) {
      p.warnungen.push(`GP weicht von Menge x EP ab (rechnerisch ${rechnerisch.toFixed(2)})`);
    }
    // EP mit echter 3. Nachkommastelle: DB speichert NUMERIC(12,2) ->
    // kaufmaennische Rundung am Server, GP bleibt wie im LV.
    if (Math.round(p.einheitspreis * 1000) % 10 !== 0) {
      p.warnungen.push('EP hat 3 Nachkommastellen - wird auf 2 gerundet gespeichert, GP bleibt wie im LV');
    }
    if (!p.einheit) p.warnungen.push('Einheit fehlt');
    if (p.pos_nr && posNrCount.get(p.pos_nr) > 1) p.warnungen.push('Positionsnummer mehrfach im Import');
    if (!p.bezeichnung) p.warnungen.push('Kurztext fehlt');
  }
}

// pdfjs lazy per CDN laden (Muster: loadXLSX in BaustellenAuswertung.js).
// Fehlerfall (offline, CDN blockiert) -> klare deutsche Meldung, kein Crash.
//
// Worker-Detail: new Worker(<CDN-URL>) ist cross-origin und wird vom Browser
// blockiert; der pdf.js-Fallback ("fake worker") liefe dann auf dem Main-
// Thread und kann den Tab einfrieren. Daher wird der Worker-Code per fetch
// geholt (CORS ok) und als same-origin Blob-URL eingehaengt.
export function loadPdfJs() {
  const withWorker = async (lib) => {
    const cur = lib.GlobalWorkerOptions.workerSrc;
    if (!cur || !String(cur).startsWith('blob:')) {
      const r = await fetch(PDFJS_WORKER_URL);
      if (!r.ok) throw new Error('worker http ' + r.status);
      const code = await r.text();
      lib.GlobalWorkerOptions.workerSrc = URL.createObjectURL(
        new Blob([code], { type: 'text/javascript' })
      );
    }
    return lib;
  };
  return new Promise((resolve, reject) => {
    const fail = () => reject(new LvParseError(
      'PDF-Bibliothek konnte nicht geladen werden (CDN nicht erreichbar). ' +
      'Bitte Internetverbindung pruefen und erneut versuchen.'
    ));
    const done = () => {
      if (!window.pdfjsLib) return fail();
      withWorker(window.pdfjsLib).then(resolve).catch(fail);
    };
    if (window.pdfjsLib) return done();
    const existing = document.querySelector('script[data-pdfjs-cdn]');
    if (existing) {
      existing.addEventListener('load', done);
      existing.addEventListener('error', fail);
      return;
    }
    const s = document.createElement('script');
    s.src = PDFJS_URL;
    s.async = true;
    s.dataset.pdfjsCdn = '1';
    s.onload = done;
    s.onerror = fail;
    document.head.appendChild(s);
  });
}

// ─── Zahlen/Text-Normalisierung ──────────────────────────────────────────────

// Deutsches Zahlenformat: "1.500,000" -> 1500.0 / "8,140" -> 8.14 / "971" -> 971.
// Liefert NaN bei allem, was nicht eindeutig eine de-DE-Zahl ist.
export function parseDeNumber(s) {
  if (typeof s !== 'string') return NaN;
  const t = s.trim();
  if (!/^\d{1,3}(\.\d{3})*(,\d+)?$/.test(t) && !/^\d+(,\d+)?$/.test(t)) return NaN;
  return Number(t.replace(/\./g, '').replace(',', '.'));
}

// Unicode-Ligaturen expandieren (pdf.js liefert je nach Font ﬀ/ﬁ/ﬂ/ﬃ/ﬄ),
// Mehrfach-Spaces zusammenfassen. Bewusst KEINE fontspezifischen Sonderfaelle.
function cleanText(s) {
  return s
    .replace(/ﬀ/g, 'ff').replace(/ﬁ/g, 'fi').replace(/ﬂ/g, 'fl')
    .replace(/ﬃ/g, 'ffi').replace(/ﬄ/g, 'ffl')
    .replace(/\s+/g, ' ')
    .trim();
}

// OZ normalisieren: "5. 6. . 1" -> "5.6..1" (Leerzeichen raus, Ziffern/Punkte bleiben)
function normalizeOz(s) {
  return s.replace(/\s+/g, '');
}

const OZ_RE = /^\d[\d.\s]*$/;                 // nur Ziffern, Punkte, Spaces
const MENGE_ME_RE = /^([\d.,]+)\s+(\S{1,6})$/; // "250,000 m" / "10 St" / "1 psch"
const SUMME_RE = /summe|gesamtbetrag|uebertrag|übertrag/i;

// ─── Seiten -> Zeilen (layout-orientiert) ────────────────────────────────────

// Eine Seite in Zeilen umbauen: Text-Runs nach y gruppieren (Toleranz 2pt),
// innerhalb der Zeile nach x sortieren. y faellt in PDF-Koordinaten nach
// unten -> absteigend sortiert = Lesereihenfolge.
async function extractPageLines(page) {
  const tc = await page.getTextContent();
  const runs = tc.items
    .filter((it) => it.str && it.str.trim() !== '')
    .map((it) => ({
      x: Math.round(it.transform[4]),
      y: Math.round(it.transform[5]),
      s: cleanText(it.str),
    }))
    .filter((r) => r.s !== '');

  const lines = [];
  for (const r of runs) {
    let line = null;
    for (const l of lines) {
      if (Math.abs(l.y - r.y) <= 2) { line = l; break; }
    }
    if (!line) { line = { y: r.y, runs: [] }; lines.push(line); }
    line.runs.push(r);
  }
  for (const l of lines) l.runs.sort((a, b) => a.x - b.x);
  lines.sort((a, b) => b.y - a.y);
  return lines;
}

// Kopf-/Fusszeilen erkennen: gleicher (normalisierter) Text auf >= 2 Seiten
// an ~gleicher y-Position (Toleranz 5pt) = wiederkehrendes Seitenelement
// (Briefkopf, Bankdaten-Footer, Spaltenkopf). Positions-/Langtextzeilen
// wiederholen sich zwar teils woertlich ("verlegen in Beton."), aber nie an
// derselben y-Position - die y-Bedingung schuetzt sie.
function buildRepeatingLineFilter(pages) {
  const seen = new Map(); // key: text -> [{y, pageIdx}]
  pages.forEach((lines, pageIdx) => {
    for (const l of lines) {
      const text = l.runs.map((r) => r.s).join(' ');
      if (!seen.has(text)) seen.set(text, []);
      seen.get(text).push({ y: l.y, pageIdx });
    }
  });
  const repeating = new Set();
  for (const [text, occ] of seen) {
    const pagesHit = new Set();
    for (const o of occ) {
      for (const p of occ) {
        if (o.pageIdx !== p.pageIdx && Math.abs(o.y - p.y) <= 5) {
          pagesHit.add(o.pageIdx); pagesHit.add(p.pageIdx);
        }
      }
    }
    if (pagesHit.size >= 2) repeating.add(text);
  }
  return (line) => repeating.has(line.runs.map((r) => r.s).join(' '));
}

// Spaltenkopf finden: Zeile mit "Ordnungszahl" + rechts davon "Menge",
// "Einheitspreis", "Gesamtpreis". Liefert die x-Anker fuer die Klassifikation
// - und ist zugleich der erste Konfidenz-Check (fehlt er: Layout unbekannt).
function findColumnAnchors(pages) {
  for (const lines of pages) {
    for (const l of lines) {
      const oz = l.runs.find((r) => /^Ordnungszahl/i.test(r.s));
      if (!oz) continue;
      const menge = l.runs.find((r) => /Menge/i.test(r.s) && r.x > oz.x);
      const ep = l.runs.find((r) => /Einheitspreis/i.test(r.s) && r.x > oz.x);
      const gp = l.runs.find((r) => /Gesamtpreis/i.test(r.s) && r.x > oz.x);
      if (menge && ep && gp) {
        return { oz: oz.x, menge: menge.x, ep: ep.x, gp: gp.x };
      }
    }
  }
  return null;
}

// ─── Zeilen-Klassifikation ───────────────────────────────────────────────────

// OZ-Zone: Runs deutlich links der Text-Spalte. Die Text-Spalte selbst haengt
// vom Template ab - als Grenze dient ozAnker + 90pt (OZ-Runs liegen in der
// analysierten Familie bei ozAnker..ozAnker+40, Text ab ozAnker+110).
function classifyLine(line, anchors) {
  const ozZoneMax = anchors.oz + 90;
  const left = line.runs.filter((r) => r.x <= ozZoneMax);
  const rest = line.runs.filter((r) => r.x > ozZoneMax);
  const text = line.runs.map((r) => r.s).join(' ');

  // Mengen-/Preiszeile: kein OZ-/Text-Spalten-Run, sondern Menge+ME im
  // Mittelbereich plus EP- und GP-Zahl an den Preis-Ankern.
  if (left.length === 0 && rest.length >= 2) {
    const mengeRun = rest.find((r) => r.x < anchors.ep - 20 && MENGE_ME_RE.test(r.s));
    const numRuns = rest.filter((r) => /^[\d.,]+$/.test(r.s));
    const epRun = numRuns.find((r) => Math.abs(r.x - anchors.ep) <= 60);
    const gpRun = [...numRuns].reverse().find((r) => r.x > anchors.ep + 40);
    if (mengeRun && epRun && gpRun && epRun !== gpRun) {
      const m = mengeRun.s.match(MENGE_ME_RE);
      return {
        art: 'menge',
        menge: parseDeNumber(m[1]),
        einheit: m[2],
        ep: parseDeNumber(epRun.s),
        gp: parseDeNumber(gpRun.s),
      };
    }
  }

  // Summen-/Uebertragszeile: Summen-Wort + Betrag in der GP-Zone, aber keine
  // Menge+ME - nur ueberspringen, nie importieren.
  if (SUMME_RE.test(text)) {
    const hasGpZahl = line.runs.some((r) => r.x > anchors.ep + 40 && /^[\d.,-]+$/.test(r.s));
    const hasMenge = line.runs.some((r) => MENGE_ME_RE.test(r.s));
    if (hasGpZahl && !hasMenge) return { art: 'summe', text };
  }

  // OZ-Zeile: linke Runs bilden zusammen eine Ordnungszahl; Rest = Kurztext.
  if (left.length > 0) {
    const ozJoined = left.map((r) => r.s).join(' ');
    if (OZ_RE.test(ozJoined)) {
      return {
        art: 'oz',
        oz: normalizeOz(ozJoined),
        ozOriginal: ozJoined,
        kurztext: rest.map((r) => r.s).join(' '),
      };
    }
    // linke Runs, aber keine OZ (z. B. Briefkopf-Reste) -> Freitext
    return { art: 'text', text };
  }

  return { art: 'text', text };
}

// ─── Kopfdaten (Seite 1, Heuristik - darf leer ausfallen) ────────────────────

function extractKopfdaten(pageLines, anchors) {
  const kopf = {
    name: null, adresse: null, plz: null, ort: null,
    auftraggeber: null, projektnummer: null, lvTitel: null, datum: null,
  };

  // Label-Wert-Paare: Wert = naechster Run rechts vom Label auf derselben
  // Zeile, sofern er nicht schon zum rechten Kontaktblock gehoert (>150pt Abstand).
  const labelValue = (label) => {
    for (const l of pageLines) {
      const idx = l.runs.findIndex((r) => r.s.toLowerCase().startsWith(label));
      if (idx !== -1 && l.runs[idx + 1] && l.runs[idx + 1].x - l.runs[idx].x < 150) {
        return { value: l.runs[idx + 1].s, line: l, valueX: l.runs[idx + 1].x };
      }
    }
    return null;
  };

  const pn = labelValue('projektnummer');
  if (pn) kopf.projektnummer = pn.value;

  // Datum: irgendwo "Datum: TT.MM.JJJJ"
  for (const l of pageLines) {
    for (const r of l.runs) {
      const m = r.s.match(/Datum:\s*(\d{2}\.\d{2}\.\d{4})/);
      if (m) { kopf.datum = m[1]; break; }
    }
    if (kopf.datum) break;
  }

  // LV-Titel: erste Zeile, die nach LV/Leistungsverzeichnis/Angebot aussieht.
  for (const l of pageLines) {
    const t = l.runs.map((r) => r.s).join(' ');
    if (/\bLV\b|leistungsverzeichnis|angebot/i.test(t) && t.length < 120) {
      kopf.lvTitel = t.replace(/Datum:.*$/, '').trim();
      break;
    }
  }

  // Bauvorhaben: Wert + Folgezeilen, deren erster Run buendig unter dem Wert
  // steht (gleiche x-Spalte, Toleranz 6pt). Daraus: letzte Zeile mit
  // "PLZ Ort"-Muster -> plz/ort, Zeile davor -> adresse, Rest -> name.
  const bv = labelValue('bauvorhaben');
  if (bv) {
    const block = [bv.value];
    const startIdx = pageLines.indexOf(bv.line);
    for (let i = startIdx + 1; i < pageLines.length && block.length < 6; i++) {
      const first = pageLines[i].runs[0];
      if (!first || Math.abs(first.x - bv.valueX) > 6) break;
      block.push(first.s);
    }
    const plzIdx = block.findIndex((z) => /^\d{5}\s+\S/.test(z));
    if (plzIdx !== -1) {
      const m = block[plzIdx].match(/^(\d{5})\s+(.+)$/);
      kopf.plz = m[1];
      kopf.ort = m[2];
      if (plzIdx >= 1) kopf.adresse = block[plzIdx - 1];
      const nameZeilen = block.slice(0, Math.max(0, plzIdx - 1));
      if (nameZeilen.length > 0) kopf.name = nameZeilen.join(' ');
    } else {
      kopf.name = block.join(' ');
    }
  }

  // Auftraggeber: oberste Zeile der Seite in der OZ-Spalte (Briefkopf-Firma).
  const first = pageLines.find((l) => l.runs[0] && Math.abs(l.runs[0].x - anchors.oz) <= 10);
  if (first) kopf.auftraggeber = first.runs.map((r) => r.s).join(' ');

  return kopf;
}

// ─── Hauptfunktion (testbar ohne Browser: nimmt ein pdfjs-Dokument) ──────────

export async function parseLvPdfDocument(doc, dateiname) {
  const pages = [];
  for (let p = 1; p <= doc.numPages; p++) {
    pages.push(await extractPageLines(await doc.getPage(p)));
  }

  const anchors = findColumnAnchors(pages);
  if (!anchors) {
    throw new LvParseError(
      'LV-Layout nicht erkannt: Spaltenkopf "Ordnungszahl / Menge / ' +
      'Einheitspreis / Gesamtpreis" nicht gefunden. Import abgebrochen - ' +
      'es wurde nichts uebernommen.'
    );
  }

  const kopf = extractKopfdaten(pages[0], anchors);

  const isRepeating = buildRepeatingLineFilter(pages);

  // Zustandsautomat ueber alle Inhaltszeilen (seitenuebergreifend):
  // pending = offener OZ-Kandidat. Mengenzeile schliesst ihn als Position ab;
  // ein neuer OZ-Kandidat davor macht ihn zur Gruppe (kategorie-Kontext),
  // seine gesammelten Textzeilen werden als Vorbemerkung gelistet.
  const positionen = [];
  const uebersprungen = [];
  let pending = null;
  let kategorie = null;
  let ozKandidaten = 0;
  let verworfen = 0;
  let freitextPuffer = null; // laufender Vorbemerkungs-Block

  const flushFreitext = () => {
    if (freitextPuffer && freitextPuffer.zeilen > 0) {
      uebersprungen.push({
        typ: 'vorbemerkung',
        seite: freitextPuffer.seite,
        zeilen: freitextPuffer.zeilen,
        auszug: freitextPuffer.text.slice(0, 150) + (freitextPuffer.text.length > 150 ? '…' : ''),
      });
    }
    freitextPuffer = null;
  };

  // Echte Gruppen-OZ ist PRAEFIX der nachfolgenden Positions-OZ
  // ("09.01" -> "09.01.010", "5.6" -> "5.6..1"). Ein Kandidat ohne
  // Mengenzeile, dessen OZ KEIN Praefix der naechsten OZ ist, ist eher eine
  // unvollstaendige Position (z. B. Zulage-/Bedarfsposition ohne Preiszeile)
  // - er darf die kategorie nicht verschmutzen und landet sichtbar unter
  // "uebersprungen" statt still zur Gruppe zu werden.
  const istOzPraefix = (a, b) =>
    b.startsWith(a) && (b.length === a.length || b[a.length] === '.' || a.endsWith('.'));

  const pendingWirdGruppe = () => {
    if (!pending) return;
    kategorie = pending.kurztext || pending.oz;
    if (pending.langtext.length > 0) {
      const text = pending.langtext.join(' ');
      uebersprungen.push({
        typ: 'vorbemerkung',
        seite: pending.seite,
        zeilen: pending.langtext.length,
        auszug: text.slice(0, 150) + (text.length > 150 ? '…' : ''),
      });
    }
    pending = null;
  };

  const pendingVerwerfen = () => {
    if (!pending) return;
    verworfen += 1;
    uebersprungen.push({
      typ: 'unvollstaendig', seite: pending.seite, zeilen: 1,
      auszug: `Position ${pending.ozOriginal} ohne Mengen-/Preiszeile`,
    });
    pending = null;
  };

  for (let pageIdx = 0; pageIdx < pages.length; pageIdx++) {
    const seite = pageIdx + 1;
    for (const line of pages[pageIdx]) {
      if (isRepeating(line)) continue;
      const c = classifyLine(line, anchors);

      if (c.art === 'oz') {
        flushFreitext();
        if (pending) {
          if (istOzPraefix(pending.oz, c.oz)) pendingWirdGruppe();
          else pendingVerwerfen();
        }
        ozKandidaten += 1;
        pending = { oz: c.oz, ozOriginal: c.ozOriginal, kurztext: c.kurztext, langtext: [], seite };
      } else if (c.art === 'menge') {
        flushFreitext();
        if (pending) {
          positionen.push({
            pos_nr: pending.oz,
            pos_nr_original: pending.ozOriginal,
            bezeichnung: pending.kurztext,
            langtext: pending.langtext.join('\n'),
            kategorie,
            einheit: c.einheit,
            sollmenge: c.menge,
            einheitspreis: c.ep,
            gesamtpreis: c.gp,
            seite: pending.seite,
          });
          pending = null;
        } else {
          // Mengenzeile ohne offene Position - unklarer Kontext.
          verworfen += 1;
          uebersprungen.push({ typ: 'unbekannt', seite, zeilen: 1, auszug: `Mengenzeile ohne Position (S. ${seite})` });
        }
      } else if (c.art === 'summe') {
        flushFreitext();
        uebersprungen.push({ typ: 'summe', seite, zeilen: 1, auszug: c.text.slice(0, 150) });
      } else {
        // Freitext: gehoert zur offenen Position (Langtext) oder ist Vorbemerkung.
        if (pending) {
          pending.langtext.push(c.text);
        } else {
          if (!freitextPuffer) freitextPuffer = { seite, zeilen: 0, text: '' };
          freitextPuffer.zeilen += 1;
          freitextPuffer.text += (freitextPuffer.text ? ' ' : '') + c.text;
        }
      }
    }
  }
  // Dokumentende: offener Kandidat ohne Mengenzeile ist unvollstaendig.
  pendingVerwerfen();
  flushFreitext();

  // ── Konfidenz-Waechter: lieber Abbruch als stiller Falsch-Import ──
  if (positionen.length === 0) {
    throw new LvParseError(
      'LV-Layout nicht erkannt: keine vollstaendige Position (OZ + Menge/EP/GP) ' +
      'gefunden. Import abgebrochen - es wurde nichts uebernommen.'
    );
  }
  if (verworfen > positionen.length) {
    throw new LvParseError(
      `LV-Layout unsicher: ${verworfen} nicht zuordenbare Bloecke bei nur ` +
      `${positionen.length} erkannten Positionen. Import abgebrochen - ` +
      'es wurde nichts uebernommen.'
    );
  }

  // ── Warnungen pro Position (Anzeige in der Vorschau, E3.1.3) ──
  // E3.2: als exportierte Funktion, damit der Tabellen-Parser
  // (lvTableParser.js) dieselben Regeln nutzt statt sie zu duplizieren.
  berechnePositionsWarnungen(positionen);

  return {
    dateiname,
    kopf,
    positionen,
    uebersprungen,
    statistik: {
      seiten: doc.numPages,
      positionen: positionen.length,
      ozKandidaten,
      verworfen,
      uebersprungeneBloecke: uebersprungen.length,
      summeGp: positionen.reduce((s, p) => s + p.gesamtpreis, 0),
    },
  };
}

// Browser-Einstieg: File -> ArrayBuffer -> pdfjs-Dokument -> Parser.
// Wirft ausschliesslich LvParseError mit deutscher, anzeigbarer Meldung.
export async function parseLvPdf(file) {
  const pdfjs = await loadPdfJs();
  const data = new Uint8Array(await file.arrayBuffer());
  let doc;
  try {
    doc = await pdfjs.getDocument({ data }).promise;
  } catch (e) {
    if (e && e.name === 'PasswordException') {
      throw new LvParseError('Die PDF ist passwortgeschuetzt und kann nicht gelesen werden.');
    }
    throw new LvParseError('Die Datei konnte nicht als PDF gelesen werden.');
  }
  try {
    return await parseLvPdfDocument(doc, file.name);
  } finally {
    try { doc.destroy(); } catch (_) { /* ignore */ }
  }
}
